import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Element } from 'react-scroll'
import { userOptionsStore } from '~/shared/api'
import { getPhoneMaskByCountry } from '~/shared/config/countryConfig'
import { TaxiparkCountryEnum } from '~/shared/config/enums'
import {
  FormProvider,
  formButtonsModel,
  TextInput,
  MaskInput,
  NumericInput,
  FormButtons,
  DatePickerInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { getFormSchema, FormValues } from './model'

export type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
}: FormProps) {
  const { t } = useTranslation()

  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting
  const isIvory = userOptionsStore.country === TaxiparkCountryEnum.CI

  const form = useForm<FormValues>({
    resolver: zodResolver(getFormSchema()),
    defaultValues,
  })

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Element name='requisites'>
        <Container>
          <ContainerTitle>{t('requisites')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={12}>
              <TextInput
                label='form.designation'
                name='name'
                placeholder='form.enter designation'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={12}>
              <TextInput
                label='form.legal address'
                name='legalAddress'
                placeholder='form.enter legal address'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={4}>
              {isIvory ? (
                <TextInput
                  label='form.inn'
                  name='inn'
                  placeholder='form.enter inn'
                  skeletonShow={isLoading}
                  readOnly={disabled}
                />
              ) : (
                <NumericInput
                  label='form.inn'
                  name='inn'
                  placeholder='form.enter inn'
                  skeletonShow={isLoading}
                  readOnly={disabled}
                  numericProps={{ thousandSeparator: '' }}
                />
              )}
            </Grid>
            <Grid xs={4}>
              {isIvory ? (
                <TextInput
                  label='form.ogrn'
                  name='ogrn'
                  placeholder='form.enter ogrn'
                  skeletonShow={isLoading}
                  readOnly={disabled}
                />
              ) : (
                <NumericInput
                  label='form.ogrn'
                  name='ogrn'
                  placeholder='form.enter ogrn'
                  skeletonShow={isLoading}
                  readOnly={disabled}
                  numericProps={{ thousandSeparator: '' }}
                />
              )}
            </Grid>
            <Grid xs={4}>
              <NumericInput
                label='form.okpo'
                name='okpo'
                placeholder='form.enter okpo'
                skeletonShow={isLoading}
                readOnly={disabled}
                numericProps={{ thousandSeparator: '' }}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='registry'>
        <Container>
          <ContainerTitle>{t('registry')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={6}>
              <TextInput
                label='form.registry permit number'
                name='freighterPermitNumber'
                placeholder='form.enter number'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
            <Grid xs={6}>
              <DatePickerInput
                label='form.placement validity period'
                name='freighterPermitExpirationDate'
                placeholder='form.select date'
                skeletonShow={isLoading}
                readOnly={disabled}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <Element name='contacts'>
        <Container>
          <ContainerTitle>{t('contacts')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={6}>
              <MaskInput
                name='phoneNumber'
                label='form.phone'
                placeholder='form.enter phone'
                skeletonShow={isLoading}
                readOnly={disabled}
                mask={getPhoneMaskByCountry()}
              />
            </Grid>
          </Grid>
        </Container>
      </Element>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
